import { useCallback } from 'react'
import { VariableSizeList as List } from 'react-window'
import { CheckboxListCheckbox } from '~/components/v2/CheckboxList/CheckboxListCheckbox'
import { useLatestCallbackRef } from '~/hooks/useLatestRef'
import { useIsSanaRebrandEnabled } from '~/theme/useIsSanaRebrandEnabled'

const HALO_ROOM_X = 12

// react-window's absolutely positioned rows are placed against the nearest positioned ancestor. Positioning the inner
// element keeps them inside the scroll container's padding.
function PositionedInner({ style, ...props }) {
  return <div {...props} style={{ ...style, position: 'relative' }} />
}

export function VirtualizedList({
  height,
  itemCount,
  itemSize,
  options,
  checkedItems,
  isDisabled,
  onChange,
  onItemsRendered,
  ref
}) {
  // Horizontal room only: a matching negative vertical margin made rows paint past the list's edge.
  const isRebrand = useIsSanaRebrandEnabled()
  const roomX = isRebrand ? HALO_ROOM_X : 0
  return (
    <List
      height={height}
      width={`calc(100% + ${roomX}px)`}
      style={{
        margin: `0 0 0 -${roomX}px`,
        padding: `0 0 0 ${roomX}px`
      }}
      innerElementType={PositionedInner}
      itemCount={itemCount}
      itemSize={itemSize}
      onItemsRendered={onItemsRendered}
      ref={ref}>
      {({ index, style }) => (
        <VirtualizedListCheckbox
          config={options[index]}
          checkedItems={checkedItems}
          isDisabled={isDisabled}
          onChange={onChange}
          index={index}
          style={style}
        />
      )}
    </List>
  )
}

VirtualizedList.displayName = 'CheckboxList.VirtualizedList'

function VirtualizedListCheckbox({ config, checkedItems, isDisabled, onChange, style }) {
  const { text, value, index: originalIndex } = config
  const onChangeProp = useLatestCallbackRef(onChange)
  /*
      eslint-disable-next-line react-hooks/preserve-manual-memoization --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: `onChangeProp` is used inside the callback but not in deps.
      Possible fix approach:
        // Option 1: Add onChangeProp to deps (it's stable so this is safe):
        const onChangeFn = useCallback(e => onChangeProp(e, originalIndex), [originalIndex, onChangeProp])

        // Option 2: Remove useCallback entirely - the memoization is not needed (verify performance is acceptable):
        const onChangeFn = e => onChangeProp(e, originalIndex)
  */
  const onChangeFn = useCallback(e => onChangeProp(e, originalIndex), [originalIndex])
  return (
    <CheckboxListCheckbox
      key={value}
      data-testid={`status-checkbox-filter-${value}`}
      style={style}
      value={value}
      isChecked={checkedItems[originalIndex]}
      isDisabled={isDisabled}
      onChange={onChangeFn}>
      {text}
    </CheckboxListCheckbox>
  )
}
